/**
 * Página de Gerenciamento de Permissões
 * Interface para administradores gerenciarem permissões de menu
 * 
 * @author Equipe GestaoeCRM
 * @date 26/09/2025
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  Shield, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Eye, 
  EyeOff,
  Check,
  X as XIcon
} from 'lucide-react';
import { menuPermissionApi } from '@/services/api';

interface MenuPermission {
  id: string;
  role: string;
  menuKey: string;
  menuName: string;
  menuIcon?: string;
  menuOrder: number;
  menuHref: string;
  canAccess: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canView: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

const PermissionsManagementPage: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState<string>('ADMIN');
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newPermission, setNewPermission] = useState<Partial<MenuPermission>>({
    role: 'ADMIN',
    menuKey: '',
    menuName: '',
    menuIcon: '',
    menuOrder: 0,
    menuHref: '',
    canAccess: true,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canView: true,
    active: true
  });

  const queryClient = useQueryClient();

  // Buscar permissões por role
  const { data: permissions, isLoading, error } = useQuery({
    queryKey: ['menu-permissions', selectedRole],
    queryFn: async () => {
      const response = await menuPermissionApi.getByRole(selectedRole);
      
      // Garantir que retornamos um array
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      } else {
        // console.error('Unexpected data structure:', response.data);
        return [];
      }
    },
    enabled: !!selectedRole
  });

  // Buscar todas as permissões para visão geral
  const { data: allPermissions } = useQuery({
    queryKey: ['all-menu-permissions'],
    queryFn: async () => {
      const response = await menuPermissionApi.getAll();
      
      // Garantir que retornamos um array
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      } else {
        // console.error('Unexpected allPermissions data structure:', response.data);
        return [];
      }
    }
  });

  // Mutação para criar permissão
  const createMutation = useMutation({
    mutationFn: (data: Partial<MenuPermission>) => menuPermissionApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['all-menu-permissions'] });
      toast.success('Permissão criada com sucesso!');
      setIsCreating(false);
      setNewPermission({
        role: selectedRole,
        menuKey: '',
        menuName: '',
        menuIcon: '',
        menuOrder: 0,
        menuHref: '',
        canAccess: true,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canView: true,
        active: true
      });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar permissão');
    }
  });

  // Mutação para atualizar permissão
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MenuPermission> }) => 
      menuPermissionApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['all-menu-permissions'] });
      toast.success('Permissão atualizada com sucesso!');
      setEditingId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar permissão');
    }
  });

  // Mutação para deletar permissão
  const deleteMutation = useMutation({
    mutationFn: (id: string) => menuPermissionApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['all-menu-permissions'] });
      toast.success('Permissão deletada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar permissão');
    }
  });

  const roles = ['ADMIN', 'GESTOR', 'ANALISTA'];

  const handleCreate = () => {
    if (!newPermission.menuKey || !newPermission.menuName || !newPermission.menuHref) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    createMutation.mutate(newPermission);
  };

  const handleUpdate = (id: string, data: Partial<MenuPermission>) => {
    updateMutation.mutate({ id, data });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Tem certeza que deseja deletar esta permissão?')) {
      deleteMutation.mutate(id);
    }
  };

  const togglePermission = (id: string, field: keyof MenuPermission) => {
    const permission = permissions?.find(p => p.id === id);
    if (permission) {
      handleUpdate(id, { [field]: !permission[field] });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-glass p-6">
        <h3 className="text-lg font-bold text-red-900 mb-2">Erro ao carregar permissões</h3>
        <p className="text-red-700">Tente recarregar a página ou verificar sua conexão.</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-text-primary mb-2 flex items-center">
          <Shield className="h-6 w-6 text-secondary mr-3" />
          Gerenciar Permissões de Menu
        </h1>
        <p className="text-text-secondary">
          Configure as permissões de acesso aos menus para cada role do sistema.
        </p>
      </div>

      {/* Filtro por Role */}
      <div className="mb-6">
        <label className="block text-sm font-bold text-text-secondary mb-2">
          Filtrar por Role:
        </label>
        <div className="flex space-x-2">
          {roles.map(role => (
            <button
              key={role}
              onClick={() => setSelectedRole(role)}
              className={`px-4 py-2 rounded-glass font-bold transition-all duration-300-all duration-300 ${
                selectedRole === role
                  ? 'bg-gradient-to-r from-primary to-secondary text-white'
                  : 'bg-background-glass-hover text-text-secondary hover:bg-background-glass-hover'
              }`}
            >
              {role}
            </button>
          ))}
        </div>
      </div>

      {/* Estatísticas */}
      {allPermissions && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {roles.map(role => {
            const rolePermissions = allPermissions?.filter((p: any) => p.role === role) || [];
            const activePermissions = rolePermissions.filter((p: any) => p.active);
            return (
              <div key={role} className="bg-background-glass backdrop-blur-glass rounded-glass shadow p-4">
                <h3 className="font-bold text-text-primary">{role}</h3>
                <p className="text-2xl font-bold text-secondary">{activePermissions.length}</p>
                <p className="text-sm text-text-secondary">menus ativos</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Botão de Nova Permissão */}
      <div className="mb-6">
        <button
          onClick={() => setIsCreating(true)}
          className="bg-gradient-to-r from-primary to-secondary text-white px-4 py-2 rounded-glass hover:brightness-110 transition-all duration-300-all duration-300 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Permissão
        </button>
      </div>

      {/* Formulário de Nova Permissão */}
      {isCreating && (
        <div className="bg-background-glass backdrop-blur-glass rounded-glass shadow-md p-6 mb-6 border border-border-glass">
          <h3 className="text-lg font-bold text-text-primary mb-4">Criar Nova Permissão</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-text-secondary mb-1">Role</label>
              <select
                value={newPermission.role}
                onChange={(e) => setNewPermission({ ...newPermission, role: e.target.value })}
                className="w-full border border-border-glass rounded-glass px-3 py-2"
              >
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-text-secondary mb-1">Menu Key *</label>
              <input
                type="text"
                value={newPermission.menuKey || ''}
                onChange={(e) => setNewPermission({ ...newPermission, menuKey: e.target.value })}
                className="w-full border border-border-glass rounded-glass px-3 py-2"
                placeholder="ex: dashboard"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-text-secondary mb-1">Nome do Menu *</label>
              <input
                type="text"
                value={newPermission.menuName || ''}
                onChange={(e) => setNewPermission({ ...newPermission, menuName: e.target.value })}
                className="w-full border border-border-glass rounded-glass px-3 py-2"
                placeholder="ex: Dashboard"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-text-secondary mb-1">Ícone</label>
              <input
                type="text"
                value={newPermission.menuIcon || ''}
                onChange={(e) => setNewPermission({ ...newPermission, menuIcon: e.target.value })}
                className="w-full border border-border-glass rounded-glass px-3 py-2"
                placeholder="ex: LayoutDashboard"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-text-secondary mb-1">Ordem</label>
              <input
                type="number"
                value={newPermission.menuOrder || 0}
                onChange={(e) => setNewPermission({ ...newPermission, menuOrder: parseInt(e.target.value) || 0 })}
                className="w-full border border-border-glass rounded-glass px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-text-secondary mb-1">Rota *</label>
              <input
                type="text"
                value={newPermission.menuHref || ''}
                onChange={(e) => setNewPermission({ ...newPermission, menuHref: e.target.value })}
                className="w-full border border-border-glass rounded-glass px-3 py-2"
                placeholder="ex: /dashboard"
              />
            </div>
          </div>
          
          {/* Permissões */}
          <div className="mt-4">
            <label className="block text-sm font-bold text-text-secondary mb-2">Permissões:</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: 'canAccess', label: 'Acesso' },
                { key: 'canView', label: 'Visualizar' },
                { key: 'canCreate', label: 'Criar' },
                { key: 'canEdit', label: 'Editar' },
                { key: 'canDelete', label: 'Deletar' },
                { key: 'active', label: 'Ativo' }
              ].map(perm => (
                <label key={perm.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!!newPermission[perm.key as keyof MenuPermission]}
                    onChange={(e) => setNewPermission({ 
                      ...newPermission, 
                      [perm.key]: e.target.checked 
                    })}
                    className="mr-2"
                  />
                  {perm.label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={() => setIsCreating(false)}
              className="px-4 py-2 text-text-secondary hover:text-gray-800"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="bg-gradient-to-r from-primary to-secondary text-white px-4 py-2 rounded-glass hover:brightness-110 disabled:opacity-50 flex items-center"
            >
              {createMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Criar
            </button>
          </div>
        </div>
      )}

      {/* Lista de Permissões */}
      <div className="bg-background-glass backdrop-blur-glass rounded-glass shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-border-glass">
          <h3 className="text-lg font-bold text-text-primary">
            Permissões para {selectedRole}
          </h3>
        </div>
        
        {permissions?.length === 0 ? (
          <div className="p-6 text-center text-text-muted">
            Nenhuma permissão encontrada para {selectedRole}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-glass-hover">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">
                    Menu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">
                    Rota
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">
                    Ordem
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">
                    Acesso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">
                    Visualizar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">
                    Criar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">
                    Editar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">
                    Deletar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">
                    Ativo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-background-glass backdrop-blur-glass divide-y divide-border-glass/50">
                {permissions?.map((permission: any) => (
                  <tr key={permission.id} className="hover:bg-background-glass-hover">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-text-primary">
                        {permission.menuName}
                      </div>
                      <div className="text-sm text-text-muted">
                        {permission.menuKey}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                      {permission.menuHref}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                      {permission.menuOrder}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => togglePermission(permission.id, 'canAccess')}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300-all duration-300 ${
                          permission.canAccess 
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-200' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-200'
                        }`}
                      >
                        {permission.canAccess ? <Check className="h-4 w-4" /> : <XIcon className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => togglePermission(permission.id, 'canView')}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300-all duration-300 ${
                          permission.canView 
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-200' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-200'
                        }`}
                      >
                        {permission.canView ? <Check className="h-4 w-4" /> : <XIcon className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => togglePermission(permission.id, 'canCreate')}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300-all duration-300 ${
                          permission.canCreate 
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-200' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-200'
                        }`}
                      >
                        {permission.canCreate ? <Check className="h-4 w-4" /> : <XIcon className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => togglePermission(permission.id, 'canEdit')}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300-all duration-300 ${
                          permission.canEdit 
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-200' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-200'
                        }`}
                      >
                        {permission.canEdit ? <Check className="h-4 w-4" /> : <XIcon className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => togglePermission(permission.id, 'canDelete')}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300-all duration-300 ${
                          permission.canDelete 
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-200' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-200'
                        }`}
                      >
                        {permission.canDelete ? <Check className="h-4 w-4" /> : <XIcon className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => togglePermission(permission.id, 'active')}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300-all duration-300 ${
                          permission.active 
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-200' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-200'
                        }`}
                      >
                        {permission.active ? <Check className="h-4 w-4" /> : <XIcon className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingId(permission.id)}
                          className="text-secondary hover:text-blue-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(permission.id)}
                          className="text-red-400 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionsManagementPage;
